import React from 'react';

import moment from 'moment';
import debounce from 'lodash/debounce';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import addDays from 'date-fns/addDays';
import subYears from 'date-fns/subYears';
import setMonth from 'date-fns/setMonth';
import differenceInDays from 'date-fns/differenceInDays';

import { DateRangePicker, OnChangeProps, Range, RangeFocus } from 'react-date-range';
import { FormattedMessage } from 'react-intl';
import { SelectValue } from 'antd/lib/select';
import { connect } from 'react-redux';
import { Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import { BlueButton, MonthSelect } from '@share/components';
import { ILoginState } from '@share/store/slices';
import { HotelsMaxDaysRange, formatHotelDate, formatToYYYYMMDD, RootState } from '@share/utils';
import { MonthsEnum } from '@share/common-types';
import { DATE_FORMAT, DEFAULT_MEDIA_POINT } from '@share/constants';
import { CloseSvg, ArrowLeftSvg, ArrowRightSvg } from '@share/assets';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
}


interface IProps extends IMapStateToProps {
  isUpdateSearch?: boolean;
  checkIn : any;
  checkOut : any;
  onDateChange: (checkIn: any, checkOut: any) => void;
  isDateValid: boolean;
  leadTime?: number;
  validate?: any;
  setIsDateSearch?: (isDateSearch: boolean) => void;
  isDateSearch?: boolean;
}

interface IState {
  isFocused: boolean;
  startDate: Date;
  initialStartDate: Date;
  endDate: Date;
  initialEndDate: Date;
  minDate: Date;
  maxDate: Date;
  key: string;
  isEndDateSelected: boolean;
  focusedRange: RangeFocus;
  isMobile: boolean;
  isInitial: boolean;
  changed: boolean;
}

const oneItem = 1;
const resizeDebounceTime = 300;
const mobileMonthCount = 12;
const fullMonthCount = 2;
const defaultRange = 0;
const isStart = 0;

class HotelDatePickerComponent extends React.Component<IProps, IState> {
  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  isInitial = true;

  constructor(props: IProps) {
    super(props);

    const now = new Date();
    const startDate = this.props.checkIn
      ? this.props.checkIn
      : new Date();
    const endDate = this.props.checkOut
    ? this.props.checkOut
    : new Date();

    this.state = {
      isFocused: false,
      startDate,
      initialStartDate: startDate,
      initialEndDate: endDate,
      endDate,
      minDate: now,
      maxDate: addYears(now, oneItem),
      key: 'selection',
      isEndDateSelected: true,
      focusedRange: [defaultRange, isStart],
      isMobile: document.body.offsetWidth <= DEFAULT_MEDIA_POINT,
      isInitial: true,
      changed: false
    };
  }

  componentDidMount(): void {
    document.addEventListener('mousedown', this.handleClickOutside);
    window.addEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps: IProps) {
    const { account } = this.props.loginStore;
    const backgroundColor = account?.backgroundColor;
    const styleBackgroundColor = !isEmpty(backgroundColor) ? { backgroundColor } : {};
    
    const root = document.documentElement;
    if (styleBackgroundColor?.backgroundColor) {
      root.style.setProperty('--dynamic-blue', styleBackgroundColor.backgroundColor);
    } else {
      root.style.setProperty('--dynamic-blue', '#0d99d6');
    }
  }

  focus = (): void => {
    if (!this.state.isFocused) {
      this.setState({ isFocused: true, changed: false });
    }
  };

  blur = (): void => {
    const { isFocused, startDate, endDate } = this.state;

    if (isFocused) {
      if (!this.state.isEndDateSelected && startDate.toISOString() === endDate.toISOString()) {
        this.setState(
          {
            startDate,
            initialStartDate: startDate,
            endDate: addDays(new Date(startDate), oneItem),
            initialEndDate: addDays(new Date(startDate), oneItem),
          },
        );
      }
      if (!this.state.isEndDateSelected &&this.props.checkIn) {
        this.props.onDateChange(startDate, addDays(new Date(startDate), oneItem),);
      }

      this.setState({
        isFocused: false,
        isEndDateSelected: true,
        focusedRange: [defaultRange, isStart],
      });
    }
  };

  setInitialValues = (): void => {
    const { endDate, startDate, initialEndDate, initialStartDate } = this.state;
    const isMoreThenMaxDays = differenceInDays(endDate, startDate) > HotelsMaxDaysRange;
    const areInitialDatesEqual = initialStartDate.toString() === initialEndDate.toString();
    const equalAfterDateAdd = areInitialDatesEqual
      ? addDays(new Date(startDate), oneItem)
      : initialEndDate;
    if (isMoreThenMaxDays) {
      this.setState({
        startDate: initialStartDate,
        endDate: equalAfterDateAdd,
      });
    }

    this.blur();
  };

  handleClickOutside = (event: MouseEvent): void => {
    if (
      (this.wrapperRef && !this.wrapperRef.current.contains(event.target as Node)) ||
      (event.target as HTMLElement).classList.contains('date-picker__fixed-bg')
    ) {
      this.setInitialValues();
    }
  };

  onResize = debounce(() => {
    if (document.body.offsetWidth <= DEFAULT_MEDIA_POINT && !this.state.isMobile) {
      const { startDate, endDate } = this.state;
      this.setState({ isMobile: true, startDate: new Date(), endDate: new Date() });

      setTimeout(() => {
        this.setState({ startDate, endDate });
      });
    } else if (document.body.offsetWidth > DEFAULT_MEDIA_POINT && this.state.isMobile) {
      this.setState({ isMobile: false });
    }
  }, resizeDebounceTime);

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside);
    window.removeEventListener('resize', this.onResize);
  }

  onDone = (): void => {
    if (this.props.isDateSearch === false && this.props.setIsDateSearch) {
      this.props.setIsDateSearch(true);
    }
    this.setState({ changed: true }, this.blur);
  };

  onSelect = (ranges: OnChangeProps): void => {
    const range = ranges as { selection: Range };
    this.isInitial = false;

    if (range.selection) {
      const { startDate, endDate } = range.selection;
      const endDateCalculated = 
        (!this.state.isEndDateSelected && startDate.toString() === endDate.toString())?
          addDays(new Date(startDate), oneItem) :
          endDate;
      this.setState(
        {
          startDate,
          endDate: endDateCalculated,
          isInitial: false,
          changed: true,
        },
      );
      if (startDate && endDateCalculated) {
        this.props.onDateChange(startDate, endDateCalculated);
      }
  }
  };

  onRangeFocusChange = (focusedRange: RangeFocus): void => {
    this.setState({ focusedRange, isEndDateSelected: !focusedRange[oneItem] });
  };

  dayContentRenderer = (date: Date) => {
    const { startDate } = this.state;

    const start = new Date(startDate);
    const startMoment = moment(start);
    const dateMoment = moment(date);

    const day = moment(date).date();
    const daysDiff = start && !this.state.isInitial && formatToYYYYMMDD(this.props.checkIn) ? dateMoment.diff(startMoment, 'days') : 0;

    if (daysDiff > 0) {
      return (
        <Tooltip placement="top" color="#0d99d6" title={`${daysDiff} day${daysDiff > 1 ? 's' : ''}`}>
          <span>{day}</span>
        </Tooltip>
      );
    }

    return (<span>{day}</span>);
  }

  getNavigatorRender = (
    currentFocusedDate: Date,
    changeShownDate: (count: number | Date, type: string) => void,
  ): React.ReactElement => {
    const today = new Date();

    const { loginStore } = this.props;
    const { account, originalUser } = loginStore;

    const leadTimeAccount = account?.leadTime;
    const leadTimeExcludeUsers = account?.leadTimeExcludeUsers;
    const leadTimeExcludeUserList = account?.leadTimeExcludeUserList;

    const leadTime = (!leadTimeExcludeUsers || !leadTimeExcludeUserList?.includes(originalUser?.userId)) ? leadTimeAccount : 0;

    const backgroundColor = account?.backgroundColor;
    const styleBackgroundColor = !isEmpty(backgroundColor) ? {  backgroundColor } : {};

    return (
      <div onMouseUp={(e) => e.stopPropagation()}>
        <div className="date-picker__arrows">
          <div
            className="date-picker__prev"
            onClick={() => changeShownDate(-oneItem, 'monthOffset')}
          >
            <ArrowLeftSvg stroke={styleBackgroundColor?.backgroundColor} />
          </div>
          <div
            className="date-picker__next"
            onClick={() => changeShownDate(+oneItem, 'monthOffset')}
          >
            <ArrowRightSvg stroke={styleBackgroundColor?.backgroundColor} />
          </div>
        </div>

        {leadTime > 0 ? (
          <div className="date-picker__lead-time-message">
            <FormattedMessage id="lead.time.message" values={{ leadTime }} />
          </div>) : null}

        <div className="date-picker__months">
          <div className="date-picker__months-left">
            <MonthSelect
              onChange={(val: SelectValue) => changeShownDate(val as number, 'setMonth')}
              value={currentFocusedDate.getMonth()}
              date={currentFocusedDate}
              minMonth={today.getMonth()}
            />
          </div>
          <div className="date-picker__months-right">
            <MonthSelect
              onChange={(val: SelectValue) => {
                if (val === MonthsEnum.January) {
                  changeShownDate(
                    setMonth(subYears(currentFocusedDate, oneItem), MonthsEnum.December),
                    'set',
                  );
                } else {
                  if (currentFocusedDate.getMonth() === MonthsEnum.December) {
                    changeShownDate(
                      setMonth(addYears(currentFocusedDate, oneItem), val as number),
                      'set',
                    );
                  } else {
                    changeShownDate((val as number) - oneItem, 'setMonth');
                  }
                }
              }}
              minMonth={today.getMonth() + oneItem}
              value={addMonths(currentFocusedDate, oneItem).getMonth()}
              date={addMonths(currentFocusedDate, oneItem)}
            />
          </div>
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { loginStore, isUpdateSearch, onDateChange, isDateValid } = this.props;
    const { account, originalUser } = loginStore;
    const { isFocused, endDate, startDate, key, focusedRange, isEndDateSelected, isMobile } = this.state;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const selectionRange = {
      startDate: start,
      endDate: end,
      key,
    };
    const hideSelection: boolean = isEndDateSelected && start?.toISOString() === end?.toISOString();
    const focusZIndex = 4;
    const blurZIndex = 1;
    const isMoreThenMaxDays = differenceInDays(end, start) > HotelsMaxDaysRange;
    const locale = account?.locale;
    const leadTimeAccount = account?.leadTime;
    const leadTimeExcludeUsers = account?.leadTimeExcludeUsers;
    const leadTimeExcludeUserList = account?.leadTimeExcludeUserList;

    const color: any = account?.searchIconsColor;

    const leadTime = (!leadTimeExcludeUsers || !leadTimeExcludeUserList.includes(originalUser?.userId)) ? leadTimeAccount : 0;

    const styleColor = !isEmpty(color)? { color } : {};  

    const generalBorderRadius: any = account?.generalBorderRadius ? account?.generalBorderRadius : null;
    const style = !isEmpty(generalBorderRadius) ? { borderRadius: generalBorderRadius } : {};
    const backgroundColor = account?.backgroundColor;
    const styleBackgroundColor = !isEmpty(backgroundColor) ? {  backgroundColor } : {};

    return (
      <div className={` hoteldatePicker  date-picker hotel-dates`} ref={this.wrapperRef}>
        <div
          className={`
            date-picker__wrapper dates-selection
            ${isFocused && isEndDateSelected ? 'selected ' : 'border-right-0'}
            ${isFocused ? 'open' : ''}
            ${!isDateValid ? 'hotelLocation_wrap_validation' : ''} 
          `}
          onClick={this.focus}
          style={{ ...style, zIndex: isFocused ? focusZIndex : blurZIndex, border: isFocused && isEndDateSelected ? `2px solid ${styleBackgroundColor?.backgroundColor}` : '2px solid #E2E2E2'}}
        >
          <div className="date-picker__left">
            <div className="date-picker__icon" style={styleColor}>
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
          </div>
          <div className="date-picker__right">
            <div className="date-picker__label">
              {isFocused ? (
                <FormattedMessage id="check.in" />) : (
                <FormattedMessage id="dates" />)}
            </div>
            <div className="date-picker__header">
              {isFocused ? (
                start && !this.state.isInitial && formatToYYYYMMDD(this.props.checkIn) ? (
                  <span className="date-picker__date">
                    {formatHotelDate(this.props.checkIn, DATE_FORMAT, locale)}
                  </span>) : (
                  <FormattedMessage id="select.date" />)
              ) : (
                this.props.checkIn && this.props.checkOut && !this.state.isInitial ? (
                  <span className="date-picker__date">
                    {formatHotelDate(this.props.checkIn, DATE_FORMAT, locale)} -{' '}
                    {formatHotelDate(this.props.checkOut, DATE_FORMAT, locale)}
                  </span>) : (
                  <FormattedMessage id="add.dates" />)
              )}
            </div>
          </div>
        </div>


        <div className="date-picker__dropdown" style={{ display: isFocused ? 'block' : 'none' }}>
          <div
            className="date-picker__mobile-header"
            style={{ display: isMobile ? 'block' : 'none' }}
          >
            {isEndDateSelected ? (
              <FormattedMessage id="check.in" />) : (
              <FormattedMessage id="check.out" />)}
          </div>

          <div className={`date-picker__wrapper hotel-dates right ${!isEndDateSelected ? 'selected' : ''} dropoffSelect`} style={{ ...style, border: isFocused && !isEndDateSelected ? `2px solid ${styleBackgroundColor?.backgroundColor}` : '2px solid #E2E2E2' }}>
            <div className="date-picker__left">
              <div className="date-picker__icon" style={styleColor}>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
            </div>
            <div className="date-picker__right">
              <div className="date-picker__label">
                <FormattedMessage id="check.out" />
              </div>
              <div className="date-picker__header">
                {end && !this.state.isInitial && isEndDateSelected && formatToYYYYMMDD(this.props.checkOut) ? (
                  <span className="date-picker__date">
                    {formatHotelDate(this.props.checkOut, DATE_FORMAT, locale)}
                  </span>) : (
                  <FormattedMessage id="select.date" />)}
              </div>
            </div>
          </div>

          <div className="date-picker__close-icon" onClick={this.setInitialValues}>
            <CloseSvg />
          </div>

          <div className={`date-picker__picker hotel-dates ${hideSelection ? 'hide-selection' : ''} ${isEndDateSelected ? '' : 'show-checkout'}`}>
            <div className="date-picker__picker-wrapper">
              <DateRangePicker
                onChange={this.onSelect}
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={isMobile ? mobileMonthCount : fullMonthCount}
                monthDisplayFormat="MMMM, yyyy"
                ranges={[selectionRange]}
                locale={locale}
                direction={isMobile ? 'vertical' : 'horizontal'}
                weekStartsOn={0}
                weekdayDisplayFormat="EEEEEE"
                preventSnapRefocus
                minDate={addDays(new Date(), leadTime)}
                maxDate={addYears(new Date(), oneItem)}
                focusedRange={focusedRange}
                onRangeFocusChange={this.onRangeFocusChange}
                dayContentRenderer={this.dayContentRenderer}
                // @ts-ignore
                navigatorRenderer={this.getNavigatorRender}
              />
            </div>
          </div>

          <div className={`date-picker__footer ${isMoreThenMaxDays ? 'right-button' : ''}`}>
            {isMoreThenMaxDays ? (
              <div className="date-picker__error-message">
                <FormattedMessage id="hotels.maximum.date.range.search.custom" values={{ number: HotelsMaxDaysRange }} />
              </div>) : null}

            <BlueButton onClick={this.onDone} disabled={isMoreThenMaxDays}>
              {!isUpdateSearch ? (<FormattedMessage id="done" />) : null}
              {isUpdateSearch ? (<FormattedMessage id="search" />) : null}
            </BlueButton>
          </div>
        </div>

        {isFocused ? <div className="date-picker__fixed-bg" /> : null}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore
  };
};


export const HotelDatePicker = connect(mapStateToProps)(HotelDatePickerComponent);
